import { apiErrorMessage, box, container, loadingButton, logo, pageSubtitle } from '@/ui/components';
import { h, defineComponent, ref } from 'vue';
import { inputField } from '@/ui/components/form';
import { initiatePasswordReset } from '@/ui/auth/auth';
import { locales, t } from '@/ui/i18n/i18n';
import { useSetLocale, useTask, useValidation } from '@/ui/concerns';
import { ForgottenPasswordUser, validate } from '@/entities/auth/forgottenPasswordUser';

interface LocalePageProps {
  locale?: locales;
}

export const InitiatePasswordResetPage = defineComponent<LocalePageProps>({
  name: 'InitiatePasswordResetPage',
  props: ['locale'],
  setup(props) {
    useSetLocale(props.locale);

    const user = ref<ForgottenPasswordUser>({ email: '' });

    const resetTask = useTask(
      () => initiatePasswordReset(user.value),
    );

    const {
      validationErrors,
      hasNoValidationErrors,
      showValidationErrors,
    } = useValidation(validate, user, resetTask.state);

    const handleSignIn = () => {
      showValidationErrors.value = true;
      if (hasNoValidationErrors.value) {
        resetTask.run();
      }
    };

    return function render() {
      const resetForm = [
        pageSubtitle(t('auth.reset-initiate')),
        h('div', { class: 'content' }, [
          h('p', t('auth.enter-sign-email')),
        ]),
        inputField({
          name: 'email',
          type: 'email',
          label: t('common.email'),
          placeholder: t('auth.enter-email'),
          value: user.value.email,
          required: true,
          errors: validationErrors.value,
          disabled: resetTask.state.value.pending,
        }, v => {
          user.value.email = v;
        }),
        apiErrorMessage(resetTask.state.value),
        loadingButton(t('auth.send-link'), handleSignIn, resetTask.state.value.pending, { expanded: true }),
      ];

      const successNote = [
        pageSubtitle(t('auth.link-on-way')),
        h('div', { class: 'content' }, [
          h('p', t('auth.link-sent')),
        ]),
      ];

      return container(
        [
          logo(),
          box([h('form', ('success' in resetTask.state.value ? successNote : resetForm))]),
          h('div', { class: 'has-text-centered' }, [
            t('auth.have-account'),
            ' ',
            h('router-link', {
              props: {
                to: {
                  name: 'login',
                  params: {
                    locale: props.locale,
                  },
                },
              },
            },
            [t('auth.sign-in')]),
          ]),
        ],
        'small',
      );
    };
  },
});
